import {
  SVButton,
  SVEmailInput,
  SVForm,
  SVLabeledText,
  SVLoader,
  SVTextInput,
} from '@/components/core';
import { useUpdateEmail } from '@/hooks/requests/useAuth';
import { Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/types/user';
import { useGetUser } from '@/hooks/requests/useUser';
import { UpdateProfileSettingsFormsProps } from '@/types/auth';
import Update2FAModal from './Update2FAModal';

type UpdateEmailFields = {
  email: string;
  code: string;
};

export default function UpdateEmailModal({
  open,
  setOpen,
}: UpdateProfileSettingsFormsProps) {
  const { user } = useGetUser();
  const [open2FAModal, setOpen2FAModal] = useState(false);
  const [isOnOtpStep, setIsOnOtpStep] = useState(false);
  const {
    startUpdateEmail,
    startUpdateEmailLoading,
    updateEmail,
    updateEmailLoading,
  } = useUpdateEmail();
  const queryClient = useQueryClient();

  const formMethods = useForm<UpdateEmailFields>({
    defaultValues: {
      email: '',
      code: '',
    },
  });

  async function handleEmailStep() {
    const data = formMethods.getValues();
    try {
      await startUpdateEmail({ email: data.email });
      setIsOnOtpStep(true);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  async function handleSubmit(data: UpdateEmailFields) {
    if (!isOnOtpStep) {
      setOpen2FAModal(true);
      return;
    }

    try {
      await updateEmail({
        email: data.email.toLowerCase(),
        otp: data.code,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail,
      });
      showToast({
        type: 'success',
        data: { content: 'Your email address has been successfully updated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="h5" component="h1">
            Update Your Email Address
          </Typography>
          <Typography>
            {isOnOtpStep
              ? 'Please enter the one time password that was sent to your new email address'
              : "We'll use your email address to send you information about your accounts and your online profile."}
          </Typography>
          {!isOnOtpStep && (
            <SVLabeledText
              label="Current Email Address"
              text={user?.email || ''}
            />
          )}
          <SVForm<UpdateEmailFields>
            methods={formMethods}
            onSubmit={handleSubmit}>
            {startUpdateEmailLoading ? (
              <SVLoader />
            ) : (
              <>
                {isOnOtpStep ? (
                  <SVTextInput
                    name="code"
                    label="Email Code"
                    htmlInputProps={{ maxLength: 6 }}
                    rules={{ required: true }}
                  />
                ) : (
                  <>
                    <SVEmailInput name="email" label="New Email" />
                    <SVEmailInput
                      name="confirmEmail"
                      label="Confirm Email"
                      validateMatch="email"
                    />
                  </>
                )}
              </>
            )}
          </SVForm>
          {isOnOtpStep && (
            <SVButton
              variant="outlined"
              isLoading={startUpdateEmailLoading}
              onClick={handleEmailStep}>
              Resend Code
            </SVButton>
          )}
          <SVButton
            type="submit"
            isLoading={updateEmailLoading}
            onClick={formMethods.handleSubmit(handleSubmit)}>
            Submit
          </SVButton>
          <Update2FAModal
            open={open2FAModal}
            setOpen={setOpen2FAModal}
            afterSuccessCallback={handleEmailStep}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
